<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Editor } from "@guolao/vue-monaco-editor";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import {
  UiAside,
  UiFormField,
  UiIcon,
  UiIconButton,
  UiInputField,
  UiMultiSelectChips,
  UiSelect,
  UiStandardButton,
  UiTabs,
  UiTabsItem,
  UiTextarea,
  UiText,
  useApi,
  useSharedLink,
  useToaster,
} from "@shopexperts/shared";
import type { AdminPage, CreatePageRequest, PageMetafields, UpdatePageRequest } from "@shopexperts/shared";
import type * as MonacoEditor from "monaco-editor";
import { usePageTags } from "../composables/usePageTags";
import { getAdminPagesEditorConfig } from "../config";
import { resolveCustomizedPagePreviewLocation } from "../utils/previewUrl";

const TYPE_TAG_PREFIX = "Type:";
const STARTER_TYPES = ["Pricing Guide", "Experts"];

type PageSort = "updated_at" | "created_at" | "path" | "title" | "status";
type SortDirection = "asc" | "desc";
type PageStatus = "draft" | "published";
interface CustomizedPagePreviewPayload {
  path: string;
  title: string;
  description: string;
  seo_title: string;
  seo_description: string;
  metafields: PageMetafields;
  sections: unknown[];
}
interface CustomizedPageInspectorAction {
  type: "show-in-editor";
  path: string;
  label: string;
  nonce: number;
}
type JsonPathSegment = string | number;

const api = useApi();
const toaster = useToaster();
const queryClient = useQueryClient();
const route = useRoute();
const router = useRouter();
const { tags: availableTags } = usePageTags();
const editorConfig = getAdminPagesEditorConfig();
const {
  url: customizedPagePreviewUrl,
  origin: customizedPagePreviewOrigin,
} = resolveCustomizedPagePreviewLocation(editorConfig.profilePreviewUrl, editorConfig.publicSiteUrl);

const search = ref("");
const debouncedSearch = ref("");
const selectedTags = ref<string[]>([]);
const sort = ref<PageSort>("updated_at");
const direction = ref<SortDirection>("desc");
const statusFilter = ref<PageStatus | "">("");
const filtersOpen = ref(false);
const editorOpen = ref(false);
const editorLoading = ref(false);
const editorPanelOpen = ref(true);
const editorTab = ref<"fields" | "sections">("fields");
const editingPage = ref<AdminPage | null>(null);
const sentinelRef = useTemplateRef<HTMLElement>("sentinelRef");
const previewIframe = useTemplateRef<HTMLIFrameElement>("previewIframe");
let observer: IntersectionObserver | null = null;
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let editorLoadToken = 0;

const path = ref("");
const status = ref<PageStatus>("draft");
const typeName = ref("");
const pageTags = ref<string[]>([]);
const tagDraft = ref("");
const title = ref("");
const description = ref("");
const seoTitle = ref("");
const seoDescription = ref("");
const editorValue = ref("[]");
const jsonError = ref("");
const isSyncingFromFields = ref(false);
const baseMetafields = ref<PageMetafields>({});
const sectionsEditor = shallowRef<MonacoEditor.editor.IStandaloneCodeEditor | null>(null);
const monacoApi = shallowRef<typeof MonacoEditor | null>(null);
const jsonHighlightDecorations = ref<string[]>([]);
let jsonHighlightTimer: ReturnType<typeof setTimeout> | null = null;
let lastHandledInspectorNonce: number | null = null;

const { data: previewPage, partnerReady: previewConnected } = useSharedLink<CustomizedPagePreviewPayload>({
  iframeRef: previewIframe,
  name: "customized-page-preview",
  mode: "providerToClient",
  targetOrigin: customizedPagePreviewOrigin,
  debounceMs: 120,
  initial: {
    path: "",
    title: "",
    description: "",
    seo_title: "",
    seo_description: "",
    metafields: {},
    sections: [],
  },
  debug: false,
});

const { data: previewInspectorAction } = useSharedLink<CustomizedPageInspectorAction | null>({
  iframeRef: previewIframe,
  name: "customized-page-preview-inspector",
  mode: "bidirectional",
  targetOrigin: customizedPagePreviewOrigin,
  initial: null,
  debug: false,
});

const sortOptions = [
  { value: "updated_at", label: "Last updated" },
  { value: "created_at", label: "Created" },
  { value: "path", label: "Path" },
  { value: "title", label: "Title" },
  { value: "status", label: "Status" },
];

const statusOptions = [
  { value: "", label: "Any status" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

const editorStatusOptions = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const typeTags = computed(() => availableTags.value.filter(isTypeTag));
const displayedTypeTags = computed(() => {
  const tags = new Set(STARTER_TYPES.map(toTypeTag));
  for (const tag of typeTags.value) tags.add(tag);
  return Array.from(tags);
});
const nonTypeTags = computed(() => availableTags.value.filter((tag) => !isTypeTag(tag)));

const filterTagOptions = computed(() =>
  nonTypeTags.value.map((tag) => ({ value: tag, label: tag })),
);

const editorTagOptions = computed(() =>
  Array.from(new Set([...nonTypeTags.value, ...pageTags.value]))
    .filter((tag) => !isTypeTag(tag))
    .sort((a, b) => a.localeCompare(b))
    .map((tag) => ({ value: tag, label: tag })),
);

const selectedRegularTags = computed<string[]>({
  get: () => selectedTags.value.filter((tag) => !isTypeTag(tag)),
  set: (tags) => {
    selectedTags.value = [...selectedTypeTags.value, ...tags];
  },
});
const selectedTypeTags = computed(() => selectedTags.value.filter(isTypeTag));
const selectedTagLabels = computed(() =>
  selectedTags.value.map((tag) => ({ value: tag, label: isTypeTag(tag) ? typeFromTag(tag) : tag })),
);

const queryKey = computed(() => [
  "admin",
  "pages",
  "customized",
  {
    search: debouncedSearch.value,
    tags: [...selectedTags.value].sort(),
    sort: sort.value,
    direction: direction.value,
    status: statusFilter.value,
  },
]);

const pagesQuery = useInfiniteQuery({
  queryKey,
  queryFn: ({ pageParam }) =>
    api.admin.pages.listPages({
      query: {
        limit: 20,
        cursor: pageParam,
        no_schema: true,
        search: debouncedSearch.value || undefined,
        tags: selectedTags.value.length ? selectedTags.value : undefined,
        sort: sort.value,
        direction: direction.value,
        status: statusFilter.value || undefined,
      },
    }),
  getNextPageParam: (lastPage) => (
    lastPage.pagination.has_next ? lastPage.pagination.cursor || undefined : undefined
  ),
  initialPageParam: undefined as string | undefined,
  staleTime: 60 * 1000,
});

const pages = computed(() => pagesQuery.data.value?.pages.flatMap((page) => page.data) || []);
const totalPages = computed(() => pagesQuery.data.value?.pages[0]?.meta?.total ?? pages.value.length);
const isListEmpty = computed(() => !pagesQuery.isLoading.value && pages.value.length === 0);
const isInitialLoading = computed(() => pagesQuery.isLoading.value);
const hasListError = computed(() => Boolean(pagesQuery.error.value));
const isFetchingNextPage = computed(() => pagesQuery.isFetchingNextPage.value);
const hasNextPage = computed(() => Boolean(pagesQuery.hasNextPage.value));
const tableColumns = "minmax(220px,1.5fr) minmax(280px,2fr) minmax(120px,150px) minmax(110px,140px) minmax(150px,170px) 56px";
const previewPayload = computed<CustomizedPagePreviewPayload>(() => {
  const sections = safeParseEditorSections();
  const metafields: PageMetafields = {
    ...baseMetafields.value,
    title: title.value,
    description: description.value,
    seo_title: seoTitle.value,
    seo_description: seoDescription.value,
    sections,
  };

  return {
    path: path.value,
    title: title.value,
    description: description.value,
    seo_title: seoTitle.value,
    seo_description: seoDescription.value,
    metafields,
    sections,
  };
});

const savePageMutation = useMutation({
  mutationFn: async () => {
    const metafields = buildEditorMetafields();
    const tags = buildPageTags();

    if (editingPage.value) {
      const body: UpdatePageRequest = {
        path: path.value.trim(),
        status: status.value,
        tags,
        metafields,
      };
      return api.admin.pages.updatePage({ params: { id: editingPage.value.id }, body });
    }

    const body: CreatePageRequest = {
      path: path.value.trim(),
      status: status.value,
      tags,
      metafields,
    };
    return api.admin.pages.createPage({ body });
  },
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "pages"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "pages", "tags"] }),
    ]);
    toaster.create({
      title: editingPage.value ? "Page updated" : "Page created",
      type: "success",
    });
    closeEditor();
  },
  onError: (error: any) => {
    toaster.create({
      title: "Failed to save page",
      description: error?.response?.data?.message || error?.message || "Please review the page details and try again.",
      type: "error",
    });
  },
});

const isSaveDisabled = computed(() => editorLoading.value && !editingPage.value);

watch(search, (value) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    debouncedSearch.value = value.trim();
  }, 300);
});

watch(sentinelRef, (newEl, oldEl) => {
  if (!observer) return;
  if (oldEl) observer.unobserve(oldEl);
  if (newEl) observer.observe(newEl);
});

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    if (pagesQuery.hasNextPage.value && !pagesQuery.isFetchingNextPage.value) {
      pagesQuery.fetchNextPage();
    }
  }, { rootMargin: "240px" });

  if (sentinelRef.value) observer.observe(sentinelRef.value);
});

onBeforeUnmount(() => {
  setAdminSidebarHidden(false);
  if (searchTimer) clearTimeout(searchTimer);
  if (jsonHighlightTimer) clearTimeout(jsonHighlightTimer);
  observer?.disconnect();
});

watchEffect(() => {
  previewPage.value = previewPayload.value;
});

watch(editorOpen, (isOpen) => {
  setAdminSidebarHidden(isOpen);

  if (isOpen) {
    editorPanelOpen.value = true;
    editorTab.value = "fields";
  }
});

watch(editorPanelOpen, () => {
  nextTick(() => {
    previewIframe.value?.contentWindow?.dispatchEvent(new Event("resize"));
  });
});

watch(
  () => [route.name, route.params.pageId],
  () => {
    void syncEditorFromRoute();
  },
  { immediate: true },
);

watch(previewInspectorAction, (action) => {
  if (action?.type === "show-in-editor") {
    if (action.nonce === lastHandledInspectorNonce) return;
    lastHandledInspectorNonce = action.nonce;
    void showPreviewPathInEditor(action);
  }
});

function isTypeTag(tag: string): boolean {
  return tag.startsWith(TYPE_TAG_PREFIX);
}

function typeFromTag(tag: string): string {
  return tag.slice(TYPE_TAG_PREFIX.length).trim();
}

function toTypeTag(type: string): string {
  return `${TYPE_TAG_PREFIX}${type.trim()}`;
}

function pageType(page: AdminPage): string {
  const tag = page.tags.find(isTypeTag);
  return tag ? typeFromTag(tag) || "-" : "-";
}

function typeTone(type: string): string {
  if (type === "Pricing Guide") return "pricing";
  if (type === "Experts") return "experts";
  return "unknown";
}

function isTypeSelected(tag: string): boolean {
  return selectedTags.value.includes(tag);
}

function toggleTag(tag: string) {
  if (selectedTags.value.includes(tag)) {
    selectedTags.value = selectedTags.value.filter((item) => item !== tag);
    toaster.create({
      title: "Type filter removed",
      description: typeFromTag(tag),
      type: "info",
    });
  } else {
    selectedTags.value = [...selectedTags.value, tag];
    toaster.create({
      title: "Type filter applied",
      description: typeFromTag(tag),
      type: "success",
    });
  }
}

function removeSelectedTag(tag: string) {
  selectedTags.value = selectedTags.value.filter((item) => item !== tag);
  toaster.create({
    title: "Filter removed",
    description: isTypeTag(tag) ? typeFromTag(tag) : tag,
    type: "info",
  });
}

function clearFilters() {
  const hadFilters = selectedTags.value.length || search.value || debouncedSearch.value || statusFilter.value;
  selectedTags.value = [];
  search.value = "";
  debouncedSearch.value = "";
  statusFilter.value = "";
  if (hadFilters) {
    toaster.create({
      title: "Filters cleared",
      type: "info",
    });
  }
}

function pageTitle(page: AdminPage): string {
  const value = page.metafields.title;
  return typeof value === "string" && value.trim() ? value : page.path;
}

function pageDescription(page: AdminPage): string {
  const value = page.metafields.description;
  if (typeof value !== "string") return "-";
  const cleaned = stripHtml(value);
  return cleaned || "-";
}

function stripHtml(value: string): string {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

async function syncEditorFromRoute() {
  if (route.name === "admin-customized-pages-create") {
    editorLoadToken += 1;
    editingPage.value = null;
    resetEditor();
    editorOpen.value = true;
    editorLoading.value = false;
    await nextTick();
    window.scrollTo({ top: 0 });
    return;
  }

  if (route.name === "admin-customized-pages-edit") {
    const pageId = Number(route.params.pageId);
    if (!Number.isFinite(pageId)) {
      await router.replace({ name: "admin-customized-pages" });
      return;
    }

    await loadEditorPage(pageId);
    return;
  }

  editorLoadToken += 1;
  editorLoading.value = false;
  editorOpen.value = false;
  editingPage.value = null;
  resetEditor();
}

async function loadEditorPage(pageId: number) {
  const loadToken = ++editorLoadToken;
  editorLoading.value = true;
  editorOpen.value = true;
  editingPage.value = null;
  resetEditor();
  await nextTick();
  window.scrollTo({ top: 0 });

  try {
    const page = await api.admin.pages.getPage({ params: { id: pageId } });
    if (loadToken !== editorLoadToken) return;
    hydrateEditorFromPage(page);
  } catch (error: any) {
    if (loadToken !== editorLoadToken) return;
    toaster.create({
      title: "Failed to load page",
      description: error?.response?.data?.message || error?.message || "The customized page could not be loaded.",
      type: "error",
    });
    await router.replace({ name: "admin-customized-pages" });
  } finally {
    if (loadToken === editorLoadToken) {
      editorLoading.value = false;
    }
  }
}

function openCreateEditor() {
  toaster.create({
    title: "Creating customized page",
    type: "info",
  });
  void router.push({ name: "admin-customized-pages-create" });
}

function openEditEditor(page: AdminPage) {
  toaster.create({
    title: "Opening customized page",
    description: page.path,
    type: "info",
  });
  void router.push({ name: "admin-customized-pages-edit", params: { pageId: page.id } });
}

function hydrateEditorFromPage(page: AdminPage) {
  editingPage.value = page;
  path.value = page.path;
  status.value = page.status;
  typeName.value = pageType(page) === "-" ? "" : pageType(page);
  pageTags.value = page.tags.filter((tag) => !isTypeTag(tag));
  title.value = stringMetafield(page.metafields.title);
  description.value = stringMetafield(page.metafields.description);
  seoTitle.value = stringMetafield(page.metafields.seo_title);
  seoDescription.value = stringMetafield(page.metafields.seo_description);
  setEditorSections(page.metafields);
  editorOpen.value = true;
}

function closeEditor() {
  void router.push({ name: "admin-customized-pages" });
}

function handleSaveClick() {
  if (jsonError.value) {
    toaster.create({
      title: "Fix Sections JSON before saving",
      description: jsonError.value,
      type: "error",
    });
    return;
  }
  savePageMutation.mutate();
}

function refreshPreview() {
  if (previewIframe.value) {
    const url = new URL(customizedPagePreviewUrl);
    url.searchParams.set("_previewRefresh", String(Date.now()));
    previewIframe.value.src = url.toString();
  }
  toaster.create({
    title: "Preview refreshed",
    type: "info",
  });
}

function applyFilters() {
  filtersOpen.value = false;
  toaster.create({
    title: "Filters applied",
    type: "success",
  });
}

function resetEditor() {
  path.value = "";
  status.value = "draft";
  typeName.value = "";
  pageTags.value = [];
  tagDraft.value = "";
  title.value = "";
  description.value = "";
  seoTitle.value = "";
  seoDescription.value = "";
  baseMetafields.value = {};
  setEditorSections({});
}

function setEditorSections(value: PageMetafields) {
  isSyncingFromFields.value = true;
  baseMetafields.value = { ...value };
  const sections = Array.isArray(value.sections) ? value.sections : [];
  editorValue.value = JSON.stringify(sections, null, 2);
  jsonError.value = "";
  nextTick(() => {
    isSyncingFromFields.value = false;
  });
}

function stringMetafield(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function parseEditorSections(): unknown[] {
  const parsed = JSON.parse(editorValue.value);
  if (!Array.isArray(parsed)) {
    throw new Error("Sections JSON must be an array.");
  }
  return parsed;
}

function safeParseEditorSections(): unknown[] {
  try {
    return parseEditorSections();
  } catch {
    return [];
  }
}

function onEditorUpdate(value: string) {
  editorValue.value = value;
  if (isSyncingFromFields.value) return;

  try {
    parseEditorSections();
    jsonError.value = "";
  } catch (error: any) {
    jsonError.value = error?.message || "Invalid JSON.";
  }
}

function buildEditorMetafields(): PageMetafields {
  return {
    ...baseMetafields.value,
    title: title.value,
    description: description.value,
    seo_title: seoTitle.value,
    seo_description: seoDescription.value,
    sections: parseEditorSections(),
  };
}

function buildPageTags(): string[] {
  const nextTags = pageTags.value
    .map((tag) => tag.trim())
    .filter((tag) => tag && !isTypeTag(tag));

  const type = typeName.value.trim();
  if (type) nextTags.push(toTypeTag(type));

  return Array.from(new Set(nextTags));
}

function addDraftTag() {
  const parts = tagDraft.value
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag && !isTypeTag(tag));

  if (!parts.length) return;
  pageTags.value = Array.from(new Set([...pageTags.value, ...parts]));
  tagDraft.value = "";
  toaster.create({
    title: parts.length === 1 ? "Tag added" : "Tags added",
    description: parts.join(", "),
    type: "success",
  });
}

function setAdminSidebarHidden(hidden: boolean) {
  window.dispatchEvent(new CustomEvent("shopexperts:admin-sidebar-hidden", { detail: hidden }));
}

function onSectionsEditorMount(
  editor: MonacoEditor.editor.IStandaloneCodeEditor,
  monaco: typeof MonacoEditor,
) {
  sectionsEditor.value = editor;
  monacoApi.value = monaco;
}

async function showPreviewPathInEditor(action: CustomizedPageInspectorAction) {
  editorPanelOpen.value = true;
  editorTab.value = "sections";
  await nextTick();

  const editor = await waitForSectionsEditor();
  const monaco = monacoApi.value;
  const model = editor?.getModel();
  if (!editor || !monaco || !model) {
    showInspectorError("The sections editor is not ready yet.");
    return;
  }

  const match = findJsonPathRange(editorValue.value, action.path);
  if (!match) {
    showInspectorError("The sections JSON changed or is invalid.");
    return;
  }

  const startPosition = model.getPositionAt(match.start);
  const endPosition = model.getPositionAt(match.end);
  const range = new monaco.Range(
    startPosition.lineNumber,
    startPosition.column,
    endPosition.lineNumber,
    endPosition.column,
  );

  editor.revealRangeInCenter(range);
  editor.setSelection(new monaco.Selection(
    startPosition.lineNumber,
    startPosition.column,
    startPosition.lineNumber,
    startPosition.column,
  ));
  editor.focus();

  jsonHighlightDecorations.value = editor.deltaDecorations(jsonHighlightDecorations.value, [
    {
      range,
      options: {
        className: "customized-json-highlight",
        overviewRuler: {
          color: "#1877f2",
          position: monaco.editor.OverviewRulerLane.Center,
        },
      },
    },
  ]);

  if (jsonHighlightTimer) clearTimeout(jsonHighlightTimer);
  jsonHighlightTimer = setTimeout(() => {
    if (sectionsEditor.value) {
      jsonHighlightDecorations.value = sectionsEditor.value.deltaDecorations(jsonHighlightDecorations.value, []);
    }
  }, 1800);
}

async function waitForSectionsEditor() {
  for (let attempt = 0; attempt < 12; attempt += 1) {
    if (sectionsEditor.value) return sectionsEditor.value;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return sectionsEditor.value;
}

function showInspectorError(description: string) {
  toaster.create({
    title: "Could not show field in editor",
    description,
    type: "error",
  });
}

function findJsonPathRange(source: string, path: string): { start: number; end: number } | null {
  try {
    JSON.parse(source);
  } catch {
    return null;
  }

  const segments = parseJsonPath(path);
  if (!segments.length) return null;
  return findPathValueRange(source, 0, segments);
}

function parseJsonPath(path: string): JsonPathSegment[] {
  const segments: JsonPathSegment[] = [];
  const matcher = /(?:^|\.)([A-Za-z_$][\w$-]*)|\[(\d+)\]/g;
  let match: RegExpExecArray | null;

  while ((match = matcher.exec(path)) !== null) {
    if (match[1] !== undefined) segments.push(match[1]);
    if (match[2] !== undefined) segments.push(Number(match[2]));
  }

  return segments;
}

function findPathValueRange(source: string, start: number, segments: JsonPathSegment[]): { start: number; end: number } | null {
  if (!segments.length) return null;

  const cursor = skipWhitespace(source, start);
  const [segment, ...rest] = segments;

  if (typeof segment === "number") {
    return findArraySegmentRange(source, cursor, segment, rest);
  }

  return findObjectSegmentRange(source, cursor, segment, rest);
}

function findObjectSegmentRange(source: string, start: number, key: string, rest: JsonPathSegment[]) {
  let cursor = skipWhitespace(source, start);
  if (source[cursor] !== "{") return null;
  cursor += 1;

  while (cursor < source.length) {
    cursor = skipWhitespace(source, cursor);
    if (source[cursor] === "}") return null;
    if (source[cursor] !== "\"") return null;

    const keyStart = cursor;
    const parsedKey = readJsonString(source, cursor);
    if (!parsedKey) return null;
    cursor = skipWhitespace(source, parsedKey.end);
    if (source[cursor] !== ":") return null;
    const valueStart = skipWhitespace(source, cursor + 1);
    const valueEnd = skipJsonValue(source, valueStart);
    if (valueEnd === null) return null;

    if (parsedKey.value === key) {
      if (!rest.length) return { start: keyStart, end: valueEnd };
      return findPathValueRange(source, valueStart, rest);
    }

    cursor = skipWhitespace(source, valueEnd);
    if (source[cursor] === ",") {
      cursor += 1;
      continue;
    }
    if (source[cursor] === "}") return null;
    return null;
  }

  return null;
}

function findArraySegmentRange(source: string, start: number, index: number, rest: JsonPathSegment[]) {
  let cursor = skipWhitespace(source, start);
  if (source[cursor] !== "[") return null;
  cursor += 1;

  for (let currentIndex = 0; cursor < source.length; currentIndex += 1) {
    cursor = skipWhitespace(source, cursor);
    if (source[cursor] === "]") return null;

    const valueStart = cursor;
    const valueEnd = skipJsonValue(source, valueStart);
    if (valueEnd === null) return null;

    if (currentIndex === index) {
      if (!rest.length) return { start: valueStart, end: valueEnd };
      return findPathValueRange(source, valueStart, rest);
    }

    cursor = skipWhitespace(source, valueEnd);
    if (source[cursor] === ",") {
      cursor += 1;
      continue;
    }
    if (source[cursor] === "]") return null;
    return null;
  }

  return null;
}

function skipWhitespace(source: string, index: number) {
  let cursor = index;
  while (/\s/.test(source[cursor] || "")) cursor += 1;
  return cursor;
}

function readJsonString(source: string, start: number): { value: string; end: number } | null {
  if (source[start] !== "\"") return null;
  let cursor = start + 1;
  let raw = "\"";

  while (cursor < source.length) {
    const char = source[cursor];
    raw += char;

    if (char === "\\") {
      cursor += 1;
      if (cursor >= source.length) return null;
      raw += source[cursor];
    } else if (char === "\"") {
      try {
        return { value: JSON.parse(raw), end: cursor + 1 };
      } catch {
        return null;
      }
    }

    cursor += 1;
  }

  return null;
}

function skipJsonValue(source: string, start: number): number | null {
  let cursor = skipWhitespace(source, start);
  const char = source[cursor];

  if (char === "\"") {
    const parsed = readJsonString(source, cursor);
    return parsed?.end ?? null;
  }

  if (char === "{" || char === "[") {
    const stack = [char];
    cursor += 1;

    while (cursor < source.length && stack.length) {
      const current = source[cursor];
      if (current === "\"") {
        const parsed = readJsonString(source, cursor);
        if (!parsed) return null;
        cursor = parsed.end;
        continue;
      }
      if (current === "{" || current === "[") stack.push(current);
      if (current === "}" || current === "]") {
        const expected = current === "}" ? "{" : "[";
        if (stack[stack.length - 1] !== expected) return null;
        stack.pop();
      }
      cursor += 1;
    }

    return stack.length ? null : cursor;
  }

  const literalMatch = /^(true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/.exec(source.slice(cursor));
  return literalMatch ? cursor + literalMatch[0].length : null;
}

</script>

<template>
  <main class="customized-pages-root">
    <section v-if="!editorOpen" class="customized-pages-list">
      <div class="customized-pages-header">
        <div>
          <h1>Customized Pages</h1>
          <p>Manage custom landing pages, SEO pages, and editorial content.</p>
        </div>
        <div class="customized-pages-header-actions">
          <span>{{ totalPages }} pages</span>
          <UiStandardButton
            label="Create Page"
            icon="plus-square"
            class="customized-create-button"
            @click="openCreateEditor"
          />
        </div>
      </div>

      <div class="customized-filter-card">
        <div class="customized-type-area">
          <div class="customized-filter-label">Filter by type</div>
          <div class="customized-type-pills">
            <button
              v-for="tag in displayedTypeTags"
              :key="tag"
              type="button"
              class="customized-type-pill"
              :class="[`customized-type-${typeTone(typeFromTag(tag))}`, { 'is-selected': isTypeSelected(tag) }]"
              @click="toggleTag(tag)"
            >
              <span class="customized-type-dot"></span>
              {{ typeFromTag(tag) }}
              <span v-if="isTypeSelected(tag)" class="customized-type-check">✓</span>
            </button>
          </div>
        </div>

        <div class="customized-filter-divider"></div>

        <div class="customized-controls">
          <label class="customized-control customized-search-control">
            <span>Search pages</span>
            <div class="customized-input-shell">
              <UiIcon name="search" class="customized-input-icon" />
              <input v-model="search" type="search" placeholder="Search by title or path..." />
            </div>
          </label>

          <label class="customized-control customized-sort-control">
            <span>Sort by</span>
            <UiSelect v-model="sort" :options="sortOptions" />
          </label>

          <UiIconButton
            icon="filter"
            aria-label="Open filters"
            class="customized-filter-button"
            @click="filtersOpen = true"
          />
        </div>

        <div v-if="selectedTags.length || statusFilter || search" class="customized-selected-filters">
          <span>Selected filters</span>
          <button
            v-for="item in selectedTagLabels"
            :key="item.value"
            type="button"
            class="customized-selected-chip"
            @click="removeSelectedTag(item.value)"
          >
            {{ item.label }} <span>×</span>
          </button>
          <button v-if="statusFilter" type="button" class="customized-selected-chip" @click="statusFilter = ''">
            {{ statusFilter }} <span>×</span>
          </button>
          <button v-if="search" type="button" class="customized-selected-chip" @click="search = ''">
            Search: {{ search }} <span>×</span>
          </button>
          <button type="button" class="customized-clear-button" @click="clearFilters">Clear</button>
        </div>
      </div>

      <div class="customized-table-card">
        <div class="customized-table-scroll">
          <div class="customized-table-head" :style="{ gridTemplateColumns: tableColumns }">
            <span>Page title</span>
            <span>Description</span>
            <span>Type</span>
            <span>Status</span>
            <span>Updated</span>
            <span></span>
          </div>

          <button
            v-for="page in pages"
            :key="page.id"
            type="button"
            class="customized-table-row"
            :class="`customized-row-${typeTone(pageType(page))}`"
            :style="{ gridTemplateColumns: tableColumns }"
            @click="openEditEditor(page)"
          >
            <span class="customized-title-cell">
              <span>{{ pageTitle(page) }}</span>
              <small>{{ page.path }}</small>
            </span>
            <span class="customized-description-cell">{{ pageDescription(page) }}</span>
            <span>
              <span v-if="pageType(page) !== '-'" class="customized-table-type" :class="`customized-type-${typeTone(pageType(page))}`">
                {{ pageType(page) }}
              </span>
              <span v-else class="customized-empty-type">-</span>
            </span>
            <span class="customized-status-cell">
              <span class="customized-status-dot" :class="page.status === 'published' ? 'is-published' : 'is-draft'"></span>
              {{ page.status }}
            </span>
            <span class="customized-updated-cell">
              <span>{{ formatDate(page.updated_at) }}</span>
              <small v-if="page.last_editor">by {{ page.last_editor.name }}</small>
            </span>
            <span class="customized-row-action">
              <UiIcon name="pencil" class="h-4 w-4" />
            </span>
          </button>

          <div v-if="isInitialLoading" class="customized-table-message">Loading customized pages...</div>
          <div v-else-if="hasListError" class="customized-table-message is-error">Failed to load pages.</div>
          <div v-else-if="isListEmpty" class="customized-table-message">No customized pages match these filters.</div>

          <div ref="sentinelRef" class="customized-sentinel">
            <span v-if="isFetchingNextPage || hasNextPage" class="customized-spinner" aria-hidden="true"></span>
            <span v-if="isFetchingNextPage">Loading more pages...</span>
            <span v-else-if="hasNextPage">Scroll to load more pages</span>
            <span v-else-if="pages.length">All pages loaded</span>
          </div>
        </div>
      </div>
    </section>

    <section v-else class="customized-pages-editor-shell flex h-[calc(100vh-72px)] w-full min-w-0 flex-1 flex-col overflow-hidden bg-white">
      <header class="flex items-center justify-between border-b border-se-gray-200 px-8 py-4">
        <div class="flex min-w-0 items-center gap-3">
          <UiIconButton icon="arrow-narrow-left" aria-label="Back to customized pages" variant="ghost" @click="closeEditor" />
          <div class="min-w-0">
            <h1 class="truncate text-2xl font-semibold text-se-dark">{{ route.name === 'admin-customized-pages-edit' ? 'Edit Customized Page' : 'Create Customized Page' }}</h1>
            <p class="text-sm text-se-gray-700">{{ editorLoading ? 'Loading customized page...' : editingPage ? editingPage.path : 'Draft a new page backed by template metafields.' }}</p>
          </div>
        </div>
        <button
          type="button"
          class="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md bg-se-primary px-4 py-2 font-semibold text-se-dark transition-colors hover:bg-se-primary/90 disabled:cursor-not-allowed disabled:bg-se-gray-700 disabled:text-se-gray-300"
          :disabled="isSaveDisabled"
          @click="handleSaveClick"
        >
          <UiIcon name="check" class="h-4 w-4" />
          <span>{{ editingPage ? 'Save Page' : 'Create Page' }}</span>
        </button>
      </header>

      <div class="customized-pages-editor-grid min-h-0 flex-1" :class="{ 'is-panel-collapsed': !editorPanelOpen }">
        <section class="customized-preview-panel">
          <div class="customized-preview-toolbar">
            <div>
              <UiText as="h2" variant="h5" text="Preview" />
              <UiText
                as="p"
                variant="caption-normal"
                class="text-se-gray-700"
                :text="previewConnected ? 'Live preview connected.' : 'Waiting for preview connection...'"
              />
            </div>
            <UiStandardButton
              label="Refresh"
              icon="repeat"
              variant="outline"
              size="sm"
              @click="refreshPreview"
            />
          </div>
          <iframe
            ref="previewIframe"
            :src="customizedPagePreviewUrl"
            class="customized-preview-frame"
            title="Customized page preview"
          />
        </section>

        <button
          v-if="!editorPanelOpen"
          type="button"
          aria-label="Open editor panel"
          class="customized-editor-rail-toggle is-collapsed"
          @click="editorPanelOpen = true"
        >
          <UiIcon name="arrow-narrow-left" />
        </button>

        <aside v-if="editorPanelOpen" class="customized-editor-panel">
          <div class="customized-editor-panel-header">
            <div>
              <UiText as="h2" variant="h5" text="Editor" />
              <UiText as="p" variant="caption-normal" class="text-se-gray-700" text="Update page fields or section JSON." />
            </div>
            <UiIconButton
              icon="arrow-right"
              aria-label="Collapse editor panel"
              class="customized-editor-collapse-button"
              @click="editorPanelOpen = false"
            />
          </div>

          <div v-if="editorLoading" class="flex flex-1 items-center justify-center p-8 text-se-gray-700">
            Loading customized page...
          </div>
          <UiTabs v-else v-model="editorTab" default-value="fields">
            <UiTabsItem value="fields" label="Fields">
              <form class="customized-editor-form" @submit.prevent="handleSaveClick">
                <UiFormField name="path" label="Path" :label-capitalize="false">
                  <UiInputField v-model="path" placeholder="/pricing-guide/shopify-seo" />
                </UiFormField>

                <div class="customized-editor-two-col">
                  <UiFormField name="status" label="Status" :label-capitalize="false">
                    <UiSelect v-model="status" :options="editorStatusOptions" />
                  </UiFormField>

                  <UiFormField name="type" label="Type" :label-capitalize="false">
                    <UiInputField v-model="typeName" placeholder="Pricing Guide" />
                  </UiFormField>
                </div>

                <UiFormField name="tags" label="Tags" :label-capitalize="false">
                  <UiMultiSelectChips v-model="pageTags" :options="editorTagOptions" placeholder="Select tags..." open-on-click show-chevron />
                  <div class="mt-2 flex gap-2">
                    <UiInputField v-model="tagDraft" placeholder="Add tag or comma list" @keydown.enter.prevent="addDraftTag" />
                    <UiStandardButton label="Add" variant="outline" @click="addDraftTag" />
                  </div>
                </UiFormField>

                <UiFormField name="title" label="Title" :label-capitalize="false">
                  <UiInputField v-model="title" placeholder="Page title" />
                </UiFormField>

                <UiFormField name="description" label="Description" :label-capitalize="false">
                  <UiTextarea v-model="description" placeholder="Short page description" :rows="3" />
                </UiFormField>

                <UiFormField name="seo-title" label="SEO title" :label-capitalize="false">
                  <UiInputField v-model="seoTitle" placeholder="Search title" />
                </UiFormField>

                <UiFormField name="seo-description" label="SEO description" :label-capitalize="false">
                  <UiTextarea v-model="seoDescription" placeholder="Search result description" :rows="3" />
                </UiFormField>
              </form>
            </UiTabsItem>

            <UiTabsItem value="sections" label="Sections JSON">
              <section class="customized-sections-editor">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <UiText as="p" variant="caption-normal" class="text-se-gray-700" text="Edit the template sections array stored in metafields.sections." />
                  <UiText as="span" variant="caption-normal" :class="jsonError ? 'text-red-600' : 'text-emerald-600'" :text="jsonError || 'Valid JSON'" />
                </div>
                <div class="customized-sections-editor-frame">
                  <Editor
                    v-if="editorTab === 'sections'"
                    :key="editorTab"
                    :value="editorValue"
                    language="json"
                    theme="vs"
                    height="calc(100vh - 338px)"
                    :options="{ automaticLayout: true, wordWrap: 'on', minimap: { enabled: false } }"
                    @mount="onSectionsEditorMount"
                    @update:value="onEditorUpdate"
                  />
                </div>
              </section>
            </UiTabsItem>
          </UiTabs>
        </aside>
      </div>
    </section>

    <UiAside v-model="filtersOpen" position="right">
      <div class="flex h-full flex-col gap-5 p-6">
        <div class="flex items-center justify-between">
          <UiText as="h3" variant="h4" text="Filters" />
          <UiIconButton icon="close" aria-label="Close filters" @click="filtersOpen = false" />
        </div>

        <UiFormField name="status-filter" label="Status" :label-capitalize="false">
          <UiSelect v-model="statusFilter" :options="statusOptions" />
        </UiFormField>

        <UiFormField name="tag-filter" label="Other tags" :label-capitalize="false">
          <UiMultiSelectChips v-model="selectedRegularTags" :options="filterTagOptions" placeholder="Select tags..." open-on-click show-chevron />
        </UiFormField>

        <div class="mt-auto flex gap-3 border-t border-se-gray-200 pt-4">
          <UiStandardButton label="Apply" class="flex-1" @click="applyFilters" />
          <UiStandardButton label="Clear" variant="outline" @click="clearFilters" />
        </div>
      </div>
    </UiAside>
  </main>
</template>
